import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAddRelatedGroupsMutation, endpoints } from 'store/api/yay/group'
import { useCurrentUser } from 'store/api/yay/user'

import { Link, useNavigate } from 'features/i18n/router'
import usePageModal from 'features/Modal/hooks/usePageModal'

import GroupList from 'components/GroupList'
import Button from 'components/Button'

import { MODAL_MODE } from '../config'
import { useSearchRelatableGroups } from './useSearchRelatableGroups'

import './style.scss'
import { groupToGroupContextSchema, snowplowEvent } from 'lib/analytics'

const AddRelatedGroupsModal = ({ groupId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { generatePageModalUrl } = usePageModal()
  const [selected, setSelected] = useState({})
  const selectedLength = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  )

  const currentUserId = useCurrentUser()?.data?.id
  const groupRes = endpoints?.getGroup?.useQueryState?.({
    groupId: groupId?.toString?.(),
  })

  const {
    onLoadMore,
    relatableGroups,
    isEmptyList,
    searchText,
    setSearchText,
  } = useSearchRelatableGroups({ groupId })

  const [addRelatedGroups] = useAddRelatedGroupsMutation()

  const handleSubmit = async () => {
    const relatedGroupIds = Object.keys(selected).reduce((prev, cur) => {
      if (selected[cur]) prev.push(Number(cur))
      return prev
    }, [])

    const addRelatedGroupsResponse = await addRelatedGroups({
      groupId,
      relatedGroupIds,
    })

    if (addRelatedGroupsResponse?.['data']?.result === 'success') {
      snowplowEvent.add_related_groups({
        group: groupToGroupContextSchema({
          group: groupRes.data,
          currentUserId,
        }),
        groups: relatableGroups.data
          .filter((g) => relatedGroupIds.includes(g.id))
          .map((g) => groupToGroupContextSchema({ group: g, currentUserId })),
      })

      navigate(
        generatePageModalUrl(MODAL_MODE.RELATED_GROUPS, {
          groupId,
        })
      )
    }
  }

  return (
    <div className="Modal__wrapper AddRelatedGroupsModal">
      <div className="Modal__header">
        <Link
          className="Modal__header__close Modal__header__close--back"
          to={generatePageModalUrl(MODAL_MODE.RELATED_GROUPS, {
            groupId,
          })}
        />

        <h2 className="Modal__header__h2">
          {t('add_related_groups_modal.title')}
        </h2>
      </div>

      <div className="Modal__search">
        <div className="Search">
          <input
            className="Search__input"
            placeholder={t('related_groups_modal.search_placeholder')}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      <div className="Modal__body">
        {isEmptyList ? (
          <div className="AddRelatedGroupsModal__empty">
            <b>{t('related_groups_modal.empty.title')}</b>
            <p>{t('related_groups_modal.empty.subtitle')}</p>
          </div>
        ) : (
          <GroupList
            groups={relatableGroups}
            withSelect
            selected={selected}
            onSelect={(groupId, value) => {
              setSelected((prev) => ({ ...prev, [groupId]: value }))
            }}
            onLoadMore={onLoadMore}
            descriptionMaxLine={2}
          />
        )}
      </div>
      <div className="Modal__footer">
        <Button
          buttonStyle="green"
          disabled={selectedLength === 0}
          onClick={() => handleSubmit()}
        >
          {t('common.add')} ({selectedLength})
        </Button>
      </div>
    </div>
  )
}

export default AddRelatedGroupsModal
